/** One entry in a runtime region table (`window.__APP_CONFIG__.regions`). */
export interface RegionConfig {
  /** Display name, e.g. `EMEA`. */
  label: string;

  /** Origin of the region API host, e.g. `https://we.api.dtims.app` (no path, no trailing slash). */
  apiBaseUrl: string;
}

/** Region table keyed by region key (`we`, `ae`, …). The only region allowlist an app has. */
export type RegionTable = Readonly<Record<string, Readonly<RegionConfig>>>;

/** Region keys become part of a host name (`<key>.dtims.app`), a cookie value and a route segment. */
export const REGION_KEY_PATTERN: RegExp = /^[a-z][a-z0-9]{1,15}$/;

/** Options for {@link parseRegionTable}. */
export interface RegionTableParseOptions {
  /**
   * Accepts an `apiBaseUrl` that is not an https origin, e.g. a development-only `local` region
   * on `http://localhost:*`. Called only for values that fail {@link isHttpsOrigin}.
   */
  readonly allowApiBaseUrl?: (key: string, apiBaseUrl: string) => boolean;
}

/**
 * Validates a raw region table and returns a frozen copy.
 * @param raw - The raw `regions` value from the runtime config.
 * @param app - App name used to prefix error messages, e.g. `shell-app`.
 * @param options - Optional exceptions to the https-origin rule.
 * @returns The frozen region table.
 * @throws {Error} Listing every problem found, so one deploy can fix them all.
 */
export function parseRegionTable(
  raw: unknown,
  app: string,
  options: RegionTableParseOptions = {}
): RegionTable {
  if (raw === undefined || raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error(`[${app}] window.__APP_CONFIG__.regions must be an object keyed by region.`);
  }

  const problems: Array<string> = [];
  const parsed: Record<string, Readonly<RegionConfig>> = {};
  const entries: Array<[string, unknown]> = Object.entries(raw);

  if (entries.length === 0) {
    problems.push('regions is empty');
  }

  for (const [key, value] of entries) {
    if (!REGION_KEY_PATTERN.test(key)) {
      problems.push(`regions.${key}: key must match ${REGION_KEY_PATTERN}`);
      continue;
    }
    const region: RegionConfig | null = parseRegion(key, value, problems, options);
    if (region) {
      parsed[key] = Object.freeze(region);
    }
  }

  if (problems.length > 0) {
    throw new Error(
      `[${app}] Runtime configuration is invalid.\n` +
        `Problems in window.__APP_CONFIG__: ${problems.join('; ')}.`
    );
  }

  return Object.freeze(parsed);
}

/**
 * Whether {@link key} is a region in {@link regions} (own property only, so `constructor` or
 * `__proto__` never match).
 * @param regions - The region table.
 * @param key - Candidate region key.
 * @returns True when the key is configured.
 */
export function hasRegion(regions: RegionTable, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(regions, key);
}

/**
 * Whether `value` is exactly an https origin (`https://host[:port]`).
 * @param value - Candidate API base URL.
 * @returns True when the value has no path, query, fragment or credentials.
 */
export function isHttpsOrigin(value: string): boolean {
  try {
    const url: URL = new URL(value);
    return url.protocol === 'https:' && url.origin === value;
  } catch {
    return false;
  }
}

/**
 * Whether `value` is exactly an http origin on localhost (`http://localhost[:port]`) — the
 * development-only exception to {@link isHttpsOrigin}.
 * @param value - Candidate API base URL.
 * @returns True for `http://localhost` or `http://localhost:<port>` with nothing else.
 */
export function isLocalhostHttpOrigin(value: string): boolean {
  try {
    const url: URL = new URL(value);
    return url.protocol === 'http:' && url.hostname === 'localhost' && url.origin === value;
  } catch {
    return false;
  }
}

/**
 * Validates one region entry, pushing any problems.
 * @param key - The region key.
 * @param value - The raw entry.
 * @param problems - Collected problems.
 * @param options - Optional exceptions to the https-origin rule.
 * @returns The entry, or null when a required field is unusable.
 */
function parseRegion(
  key: string,
  value: unknown,
  problems: Array<string>,
  options: RegionTableParseOptions
): RegionConfig | null {
  const entry: Partial<Record<keyof RegionConfig, unknown>> =
    value !== null && typeof value === 'object' ? value : {};
  const { label, apiBaseUrl } = entry;

  if (typeof label !== 'string' || !label.trim()) {
    problems.push(`regions.${key}.label`);
  }
  if (
    typeof apiBaseUrl !== 'string' ||
    !(isHttpsOrigin(apiBaseUrl) || options.allowApiBaseUrl?.(key, apiBaseUrl) === true)
  ) {
    problems.push(`regions.${key}.apiBaseUrl must be an https origin with no path`);
  }
  if (typeof label !== 'string' || typeof apiBaseUrl !== 'string') {
    return null;
  }

  return { label: label.trim(), apiBaseUrl };
}
